import React, { lazy, Suspense } from 'react';
import { Link, Route, Routes, useLocation } from 'react-router-dom';

// Components
import FullPageLoader from '../components/FullPageLoader';
import { Alert, Button } from 'antd';
import AuthLayout from '../components/layout/AuthLayout';

const Dashboard = lazy(() => import('../views/Dashboard'));
const Login = lazy(() => import('../views/Login'));
const NoPage = lazy(() => import('../views/404'));
const ForgotPassword = lazy(() => import('../views/ForgotPassword'));
const ResetPassword = lazy(() => import('../views/ResetPassword'));
const SuggestionBox = lazy(() => import('../views/SuggestionBox'));
const Questions = lazy(() => import('../views/Questions'));
const Answers = lazy(() => import('../views/Answers'));
const Employees = lazy(() => import('../views/Employees'));
const Tags = lazy(() => import('../views/Tags'));
const TagDetails = lazy(() => import('../views/Tags/TagDetails'));
const Profile = lazy(() => import('../views/Profile'));
const EditProfile = lazy(() => import('../views/Profile/EditProfile'));
const EditCompany = lazy(() => import('../views/Profile/EditCompany'));
const ChangePassword = lazy(() => import('../views/Profile/ChangePassword'));
const SSOView = lazy(() => import('../views/SSO'));
// const NudgeDashboard = lazy(() => import('../views/Nudge'));
const Loops = lazy(() => import('../views/Loops'));
const SurveySchema = lazy(() => import('../views/SurveySchema'));
const Payment = lazy(() => import('../views/Payment'));
const Journey = lazy(() => import('../views/Journey'));
const JourneyDetail = lazy(() => import('../views/Journey/View'));
const SurveySchemaDetail = lazy(() =>
  import('../views/SurveySchema/SurveySchemaDetail'),
);
// const WhatsappAnalytics = lazy(() => import('../views/WhatsappAnalytics'));
const Events = lazy(() => import('../views/Events'));
const EventsDashboard = lazy(() => import('../views/Events/EventsDashboard'));
const SurveyConfiguration = lazy(() =>
  import('../views/Profile/SuerveyConfiguration'),
);
const LoopConfiguration = lazy(() =>
  import('../views/Profile/LoopConfiguration'),
);
const EmailTemplates = lazy(() => import('../views/Profile/EmailTemplates'));

// utils
import { useSession } from '../Providers/SessionProviders';
import { translate } from '../i18n/i18n';

const t = translate(['common']);

const ElrinRoutes = () => {
  const { currentUser } = useSession();
  const location = useLocation();

  if (!currentUser) {
    return (
      <Suspense fallback={<FullPageLoader />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/sso/:token" element={<SSOView />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/set-password/:token" element={<ResetPassword />} />
          <Route path="*" element={<NoPage />} />
        </Routes>
      </Suspense>
    );
  }

  const allRoutes = [
    <Route
      key="view_feedbackconversation"
      path="/suggestion-box"
      element={<SuggestionBox />}
    />,
    <Route key="view_question" path="/questions" element={<Questions />} />,
    <Route key="view_answer" path="/answers" element={<Answers />} />,
    <Route key="view_useraccount" path="/employees" element={<Employees />} />,
    <Route key="view_tag" path="/tags" element={<Tags />} />,
    <Route key="view_tag" path="/tags/view/:tagId" element={<TagDetails />} />,
    // <Route
    //   key="view_nudge"
    //   path="/resolution-dashboard"
    //   element={<NudgeDashboard />}
    // />,
    <Route
      key="view_survey_schema"
      path="/survey-schema"
      element={<SurveySchema />}
    />,
    <Route key="loop" path="/loop" element={<Loops />} />,
    <Route key="view_payment" path="/payment" element={<Payment />} />,
    <Route
      key="view_survey_schema_dashboard"
      path="/survey-schema/:surveyId/dashboard"
      element={<SurveySchemaDetail />}
    />,
    // <Route
    //   key="view_whatsapp_analytics"
    //   path="/whatsapp-analytics"
    //   element={<WhatsappAnalytics />}
    // />,
    <Route key="view_journey" path="/journey" element={<Journey />} />,
    <Route
      key="view_detail_journey"
      path="/journey/view/:journeyId"
      element={<JourneyDetail />}
    />,
    <Route key="view_events" path="/events" element={<Events />} />,
    <Route
      key="view_event_dashboard"
      path="/events/:eventId/dashboard"
      element={<EventsDashboard />}
    />,
  ];

  return (
    <AuthLayout>
      {!location.pathname.includes('change-password') &&
        currentUser?.is_password_updated && (
          <Alert
            style={{ marginBottom: '10px' }}
            message={t('passwordExpiration.message')}
            description={t('passwordExpiration.changePassword')}
            type="info"
            showIcon
            action={
              <Link to="/profile/change-password">
                <Button type="primary">
                  {t('passwordExpiration.buttonText')}
                </Button>
              </Link>
            }
          />
        )}
      {!location.pathname.includes('change-password') &&
        !!currentUser?.password_expiry_days &&
        currentUser?.password_expiry_days <= 14 && (
          <Alert
            style={{ marginBottom: '10px' }}
            message={t('passwordExpiration.expiryDays.message')}
            description={
              <span
                dangerouslySetInnerHTML={{
                  __html: t('passwordExpiration.expiryDays.description', {
                    days: currentUser?.password_expiry_days,
                  }),
                }}
              />
            }
            type="info"
            closable
            showIcon
            action={
              <Link to="/profile/change-password">
                <Button type="primary">
                  {t('passwordExpiration.buttonText')}
                </Button>
              </Link>
            }
          />
        )}
      <Suspense fallback={<FullPageLoader />}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="profile" element={<Profile />}>
            <Route path="edit" element={<EditProfile />} />
            <Route path="change-password" element={<ChangePassword />} />
            <Route path="company" element={<EditCompany />} />
            <Route
              path="survey-configuration"
              element={<SurveyConfiguration />}
            />
            <Route path="loop" element={<LoopConfiguration />} />
            <Route path="email-templates" element={<EmailTemplates />} />
          </Route>
          {allRoutes}
          <Route path="*" element={<NoPage />} />
        </Routes>
      </Suspense>
    </AuthLayout>
  );
};

export default ElrinRoutes;
